import React, { useEffect, useLayoutEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { auth, db } from '../firebase';
import AuthModal from './AuthModal';
import PremiumLoader from './PremiumLoader';
import { useDeferredLoader } from '../hooks/useDeferredLoader';
import {
  contextAllowsEntitlement,
  buildStudioOnboardingAllowedPaths,
  membershipPeriodEnded,
  hasValidActiveMembership,
  shouldForceStudioOnboarding,
} from '../utils/studioAccess';
import { isStudioHubPath } from '../utils/navigationPaths';

const APP_ENV = (typeof process !== 'undefined' && process.env && process.env.REACT_APP_ENV) || 'prod';
const isStaging = APP_ENV === 'staging' || (typeof window !== 'undefined' && (window.location.hostname || '').includes('staging'));

/**
 * App.js wraps each route in its own <ProtectedRoute>, so navigating unmounts/remounts this component
 * and resets useState. Session-scoped memory restores "already checked" without showing the loader again.
 */
const membershipGateMemory = new Map();

function readGate(uid) {
  if (!uid) return null;
  return membershipGateMemory.get(uid) || null;
}

function writeGate(uid, checked, valid) {
  if (!uid) return;
  membershipGateMemory.set(uid, { checked, valid });
}

function clearGateMemory() {
  membershipGateMemory.clear();
}

export default function ProtectedRoute({ children, fallback = null }) {
  const { user, isAuthenticated, loading, authResolved, membership, membershipLoading, refreshMembership } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authMode, setAuthMode] = useState('login');
  const [forceAuthOpen, setForceAuthOpen] = useState(true);
  const [membershipValid, setMembershipValid] = useState(false);
  const [membershipChecked, setMembershipChecked] = useState(false);

  const currentPath = location.pathname || '';
  const isMentorPath = currentPath.startsWith('/mentor') || currentPath.startsWith('/mentors');
  const mentorAllowedPaths = new Set(['/mentor', '/mentor-dashboard', '/mentors', '/mentors/dashboard', '/create-books', '/magic', '/mentors/co-learning']);
  const hubPath =
    isStudioHubPath(currentPath) ||
    currentPath === '/artist-wall' ||
    currentPath === '/create-books';
  /** Firebase session or hydrated context — hub routes should not flash "Signing you in" on every navigation. */
  const sessionUid = auth?.currentUser?.uid || user?.uid || null;
  const authPending = !authResolved || loading;
  const hubHasSession = hubPath && !!sessionUid;
  const showAuthLoader = useDeferredLoader(authPending && !hubHasSession, { delayMs: 350 });

  // Restore gate from session memory before paint so a new route instance does not flash "Checking membership…".
  useLayoutEffect(() => {
    if (!user?.uid) return;
    const g = readGate(user.uid);
    if (g?.checked) {
      setMembershipChecked(true);
      setMembershipValid(!!g.valid);
    }
  }, [user?.uid]);

  // AuthContext membership + a fast Firestore read for studio-onboarding flags (avoids long /membership/status waits).
  useEffect(() => {
    if (!isAuthenticated || !user) {
      clearGateMemory();
      setMembershipChecked(false);
      setMembershipValid(false);
      return;
    }

    const uid = user.uid;
    const remembered = readGate(uid);

    const commitGate = (valid) => {
      setMembershipValid(!!valid);
      setMembershipChecked(true);
      writeGate(uid, true, !!valid);
    };

    // Only show the full-screen "checking membership…" when we have never completed a gate for this uid this session.
    if (!remembered?.checked) {
      setMembershipChecked(false);
    }

    if (currentPath === '/magic') {
      commitGate(true);
      return;
    }

    if (currentPath === '/onboarding/studio-access' || currentPath === '/onboarding/artmo-success') {
      commitGate(true);
      return;
    }

    // Activity library / stories / dashboard: same gate as Artist Corner when already entitled
    if (isStudioHubPath(currentPath) && (contextAllowsEntitlement(membership) || remembered?.valid)) {
      commitGate(true);
      return;
    }

    const accountType = (user.accountType || '').toString() || 'student';
    const isMentorAcct = accountType === 'ngo_admin' || accountType === 'art_mentor' || accountType === 'mentor';

    // Mentor setup/dashboard: allow without membership check when accountType from context is mentor-like
    if (isMentorAcct && (mentorAllowedPaths.has(currentPath) || currentPath.startsWith('/mentors'))) {
      if (isStaging) console.log('[auth] protected route: mentor path allowed from context', { accountType, path: currentPath });
      commitGate(true);
      return;
    }

    // Organization primary: allow only if membership is active (from context)
    if (accountType === 'mentor' && user.mentorType === 'organization' && user.isPrimaryId) {
      const checkOrg = async () => {
        let gateValid = false;
        try {
          const data = await Promise.race([
            refreshMembership(user.uid),
            new Promise((_, reject) => setTimeout(() => reject(new Error('timeout')), 12000)),
          ]).catch(() => null);
          if (data?.allowed && data?.membership?.isActive && !data?.membership?.isExpired) {
            gateValid = true;
            setMembershipValid(true);
          } else if (currentPath === '/organization-renewal') {
            gateValid = true;
            setMembershipValid(true);
          } else {
            navigate('/organization-renewal');
          }
        } catch {
          navigate('/organization-renewal');
        } finally {
          setMembershipChecked(true);
          writeGate(uid, true, gateValid);
        }
      };
      checkOrg();
      return;
    }

    const fetchMembershipWithTimeout = async (uid, ms) => {
      let t;
      const timeout = new Promise((_, reject) => {
        t = setTimeout(() => reject(new Error('membership_status_timeout')), ms);
      });
      try {
        const data = await Promise.race([refreshMembership(uid), timeout]);
        return data;
      } catch (e) {
        if (e?.message === 'membership_status_timeout') return null;
        return null;
      } finally {
        clearTimeout(t);
      }
    };

    /** Fast Firestore check so students who still need Studio onboarding are not blocked on HTTP membership. */
    const readUserDocQuick = async (uid, ms = 3500) => {
      try {
        const snap = await Promise.race([
          getDoc(doc(db, 'users', uid)),
          new Promise((_, reject) => setTimeout(() => reject(new Error('fs_timeout')), ms)),
        ]);
        return snap?.exists?.() ? snap.data() : {};
      } catch {
        return null;
      }
    };

    // All other routes: use only context membership (no direct API/Firestore for user doc)
    const resolveMembership = async () => {
      let grace = false;
      try {
        const ts = parseInt(localStorage.getItem('membershipJustActivatedAt') || '0', 10);
        grace = ts && (Date.now() - ts) < 120000;
      } catch (_) {}
      if (grace) {
        commitGate(true);
        return;
      }

      const studentLike = accountType === 'student' || accountType === '';

      // Firestore is the source of truth for studio onboarding flags. Stale AuthContext membership
      // right after checkout could still say needsStudioOnboarding — that wrongly sent users from
      // /artist-profile back to the "book customer" welcome. Include profile routes in the fast path.
      if (
        studentLike &&
        (isStudioHubPath(currentPath) ||
          currentPath === '/artist-wall' ||
          currentPath === '/artist-profile' ||
          currentPath === '/profile')
      ) {
        const ud = await readUserDocQuick(user.uid);
        if (shouldForceStudioOnboarding(membership, ud)) {
          navigate('/onboarding/studio-access', { replace: true });
          commitGate(true);
          return;
        }
        if (ud && (ud.onboardingCompleted === true || ud.needsStudioOnboarding === false)) {
          // Onboarding alone must not bypass an expired or inactive subscription / Artmo period.
          if (membership && membershipPeriodEnded(membership)) {
            navigate('/membership', { state: { isRenewal: true } });
            commitGate(false);
            return;
          }
          if (
            membership?.membership?.isExpired === true ||
            (membership?.membership?.status || '').toString().toLowerCase() === 'expired'
          ) {
            navigate('/membership', { state: { isRenewal: true } });
            commitGate(false);
            return;
          }
          if (contextAllowsEntitlement(membership)) {
            commitGate(true);
            return;
          }
          // Fall through: refresh membership from API so expiry / Artmo state is authoritative.
        }
      }

      if (studentLike && shouldForceStudioOnboarding(membership, null)) {
        const allowedDuring = buildStudioOnboardingAllowedPaths(membership);
        if (!allowedDuring.has(currentPath)) {
          navigate('/onboarding/studio-access', { replace: true });
          commitGate(false);
          return;
        }
        commitGate(true);
        return;
      }

      if (contextAllowsEntitlement(membership)) {
        if (isStaging) console.log('[auth] protected route: allowed from context');
        commitGate(true);
        return;
      }
      if (membership && membershipPeriodEnded(membership)) {
        navigate('/membership', { state: { isRenewal: true } });
        commitGate(false);
        return;
      }
      if (membership && !membership.allowed) {
        if (membership.isNewAccount === true) {
          if (studentLike && shouldForceStudioOnboarding(membership, null)) {
            const allow = buildStudioOnboardingAllowedPaths(membership);
            if (!allow.has(currentPath)) {
              navigate('/onboarding/studio-access', { replace: true });
            }
            commitGate(allow.has(currentPath));
            return;
          }
          // Status can still say "new account" when the membership block is empty (e.g. Artmo) but
          // studio onboarding flags are already cleared — allow the hub only if entitlement is still valid.
          if (
            studentLike &&
            (membership.onboardingCompleted === true || membership.needsStudioOnboarding === false)
          ) {
            if (membershipPeriodEnded(membership)) {
              navigate('/membership', { state: { isRenewal: true } });
              commitGate(false);
              return;
            }
            if (
              membership.membership?.isExpired === true ||
              (membership.membership?.status || '').toString().toLowerCase() === 'expired'
            ) {
              navigate('/membership', { state: { isRenewal: true } });
              commitGate(false);
              return;
            }
            if (contextAllowsEntitlement(membership)) {
              commitGate(true);
              return;
            }
          }
          navigate('/membership', { state: { isRenewal: false } });
          commitGate(false);
          return;
        }
        if (membership.membership?.isExpired || membership.membership?.status === 'expired') {
          navigate('/membership', { state: { isRenewal: true } });
          commitGate(false);
          return;
        }
      }

      let data = null;
      try {
        // Shorter first attempt so Artist Corner / hub routes feel snappy; second try catches cold API.
        data = await fetchMembershipWithTimeout(user.uid, 6000);
        if (!data) {
          await new Promise((r) => setTimeout(r, 200));
          data = await fetchMembershipWithTimeout(user.uid, 5000);
        }
      } catch (err) {
        if (isStaging) console.warn('[auth] protected route: refreshMembership failed', err?.message || err);
      }
      if (!data) {
        // Offline / transient API failure: stay usable when we have no proof of expiry; if local snapshot
        // shows the period has ended, send user to renew (same idea as Stripe/Auth0 client-side expiry checks).
        if (membership && membershipPeriodEnded(membership)) {
          navigate('/membership', { state: { isRenewal: true } });
          commitGate(false);
          return;
        }
        if (membership && hasValidActiveMembership(membership)) {
          setMembershipValid(true);
        } else {
          setMembershipValid(true);
        }
        setMembershipChecked(true);
        writeGate(uid, true, true);
        return;
      }
      if (studentLike && shouldForceStudioOnboarding(data, data)) {
        const allow = buildStudioOnboardingAllowedPaths(data);
        if (!allow.has(currentPath)) {
          navigate('/onboarding/studio-access', { replace: true });
          commitGate(false);
          return;
        }
        commitGate(true);
        return;
      }
      if (contextAllowsEntitlement(data)) {
        if (isStaging) console.log('[auth] protected route: allowed after refresh');
        commitGate(true);
        return;
      }
      if (data?.isNewAccount === true) {
        // New students should start Studio onboarding when flags say so. Do not use isNewAccount alone:
        // the status endpoint also returns isNewAccount when membership is structurally "empty" even
        // after onboarding/complete (artmo_limited) — Firestore then has onboardingCompleted + needsStudioOnboarding false.
        if (studentLike) {
          const studioDone =
            data?.onboardingCompleted === true || data?.needsStudioOnboarding === false;
          if (!studioDone) {
            if (currentPath !== '/onboarding/studio-access') {
              navigate('/onboarding/studio-access', { replace: true });
            }
            commitGate(true);
            return;
          }
          commitGate(true);
          return;
        }

        navigate('/membership', { state: { isRenewal: false } });
        commitGate(false);
        return;
      }
      if (data?.membership?.isExpired) {
        navigate('/membership', { state: { isRenewal: true } });
        commitGate(false);
        return;
      }

      const hasHistory = data?.membership?.status || data?.membership?.expiresAt || data?.membership?.planId;
      navigate('/membership', { state: { isRenewal: !!hasHistory } });
      commitGate(false);
    };

    resolveMembership();
  }, [isAuthenticated, user?.uid, currentPath, membership, refreshMembership, navigate]);

  // Mentor session bypass (no Firebase auth)
  if (isMentorPath || mentorAllowedPaths.has(currentPath)) {
    try {
      const session = JSON.parse(localStorage.getItem('mentor_session') || '{}');
      if (session?.ownerId && session?.email) return children;
      if (currentPath === '/mentors/co-learning') {
        const params = new URLSearchParams(window.location.search);
        if (params.get('code')) return children;
      }
    } catch (_) {}
  }

  // Studio hub / Artist Wall: render immediately when Firebase already has a session.
  if (hubHasSession) {
    if (isAuthenticated) {
      if (!membershipChecked) return children;
      if (membershipValid) return children;
      if (readGate(user?.uid)?.valid) return children;
      return null;
    }
    if (authPending) return children;
  }

  if (authPending) {
    if (!showAuthLoader) return null;
    return (
      <PremiumLoader
        message="One moment…"
        subtitle=""
        variant="fullscreen"
      />
    );
  }

  // After signup we set `membershipJustActivatedAt` in localStorage.
  // Sometimes ProtectedRoute can run once with `authResolved=true` but `isAuthenticated=false`
  // before AuthContext finishes hydrating. In that moment, showing AuthModal would incorrectly
  // bring up the "select your account type" UI. During this short grace window, render a
  // loading screen instead.
  let signupHydrationGrace = false;
  try {
    const ts = parseInt(localStorage.getItem('membershipJustActivatedAt') || '0', 10);
    signupHydrationGrace = ts && Date.now() - ts < 20000;
  } catch (_) {}

  if (!isAuthenticated && (isMentorPath || mentorAllowedPaths.has(currentPath))) {
    try {
      const session = JSON.parse(localStorage.getItem('mentor_session') || '{}');
      if (session?.ownerId && session?.email) return children;
    } catch (_) {}
  }

  if (isAuthenticated) {
    const stillChecking = !membershipChecked;
    if (stillChecking) {
      // Premium-app behavior: keep content visible while checks run in background.
      // Redirects still happen once checks complete, but we avoid full-screen loader flashes.
      return children;
    }
    if (membershipValid) return children;
    // Hub siblings already passed the gate on another studio route — avoid blank screen when remounting.
    if (isStudioHubPath(currentPath) && readGate(user?.uid)?.valid) return children;
    return null;
  }

  if (!fallback) {
    if (
      signupHydrationGrace &&
      (currentPath === '/onboarding/studio-access' ||
        currentPath === '/onboarding/artmo-success' ||
        currentPath.startsWith('/onboarding/') ||
        currentPath === '/artist-profile' ||
        currentPath === '/profile')
    ) {
      return (
        <PremiumLoader
          message="Preparing your studio…"
          subtitle="Setting up your Artmosphere space"
          variant="fullscreen"
        />
      );
    }

    return (
      <>
        <AuthModal
          isOpen={forceAuthOpen}
          onClose={() => {
            setForceAuthOpen(false);
            navigate('/');
          }}
          mode={authMode || 'login'}
          onModeSwitch={setAuthMode}
          redirectSignupToFullPage={false}
        />
      </>
    );
  }
  return fallback;
}
